import { Trans } from '@lingui/macro';
import { msg, Plural } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { MultipleCheckbox } from './MultipleCheckbox';
import { MetricField, MetricUnit, Reports_GetDevicesQuery } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { ALLOWED_METRIC_FIELDS } from '@features/reports2/utils/allowedMetricFields';
import { getUnitsPerMetric } from '@/utils/getUnitsPerMetric';
import { getMetricFieldAttributes } from '@/utils/attributes/metricField';
import Fuse from 'fuse.js';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InnerPaper } from '../common';

interface Props {
  data: Reports_GetDevicesQuery;
  selectedDeviceIds: string[];
  selectedMetrics: Metric[];
  onChangeSelectedMetrics: (metrics: Metric[]) => void;
}

type Metric = {
  name: MetricField;
  unit: MetricUnit | null;
};

type MetricOption = {
  name: MetricField;
  label: string;
  unit: MetricUnit | null;
  options: { label: string; value: MetricUnit }[];
  category: 'basic' | 'advanced';
};

const CENTIMETER_FIELDS: MetricField[] = [
  MetricField.Level,
  MetricField.OriginalDistance,
  MetricField.OriginalDistanceRaw,
  MetricField.Position,
  MetricField.LevelRaw,
  MetricField.SnowLevel,
  MetricField.SnowLevelRaw,
  MetricField.SnowLevelNonCalibrated,
  MetricField.SnowWaterEquivalent,
  MetricField.SnowWaterEquivalentRaw,
  MetricField.SnowWaterEquivalentNonCalibrated,
];

const CUBIC_METER_FIELDS: MetricField[] = [
  MetricField.VolumeM3,
  MetricField.VolumeDay,
  MetricField.VolumeHour,
  MetricField.VolumeMonth,
  MetricField.StoredVolume,
  MetricField.CalculatedVolume,
  MetricField.AccumulatedVol,
  MetricField.AccumulatedVolRaw,
  MetricField.OriginalVol,
  MetricField.OriginalVolRaw,
  MetricField.AccumulatedVolume,
  MetricField.DgaAccumulatedVolume,
];

const ROW_HEIGHT = 40;

const getDefaultUnit = (field: MetricField, i18n: I18n): MetricUnit | null => {
  const units = getUnitsPerMetric(field, i18n);
  if (CENTIMETER_FIELDS.includes(field)) return MetricUnit.Centimeter;
  if (CUBIC_METER_FIELDS.includes(field)) return MetricUnit.CubicMeter;
  return units.at(0)?.enumValue ?? null;
};

const CATEGORY_BUTTON_SX = { minWidth: 0, px: 1, py: 0, fontSize: '0.75rem', lineHeight: 1.5 };

export const MetricsInput: FC<Props> = ({
  data,
  selectedDeviceIds,
  selectedMetrics,
  onChangeSelectedMetrics,
}) => {
  const { i18n, _ } = useLingui();
  const [searchMetrics, setSearchMetrics] = useState('');
  const [metricOptions, setMetricOptions] = useState<MetricOption[]>([]);
  const [basicExpanded, setBasicExpanded] = useState(true);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);

  const isSearching = searchMetrics.length > 0;

  useEffect(() => {
    const fieldsSet = new Set<MetricField>();
    data?.devices
      .filter((device) => selectedDeviceIds.includes(device.id))
      .forEach(({ metrics }) => {
        metrics.availableFields.forEach((field) => {
          if (ALLOWED_METRIC_FIELDS.includes(field)) {
            fieldsSet.add(field);
          }
        });
      });

    const validMetrics = selectedMetrics.filter((metric) => fieldsSet.has(metric.name));

    if (validMetrics.length !== selectedMetrics.length) {
      onChangeSelectedMetrics(validMetrics);
    }

    setMetricOptions((oldOptions) => {
      const previousNames = new Set(oldOptions.map((o) => o.name));
      // oldOptions=[] + validMetrics non-empty = component re-mounted (user navigated away and back)
      // In that case, preserve the existing selection — do not auto-select additional metrics.
      const isRemount = oldOptions.length === 0 && validMetrics.length > 0;
      if (!isRemount) {
        const selectedNames = new Set(validMetrics.map((m) => m.name));
        const newMetrics = Array.from(fieldsSet)
          .filter((field) => {
            if (previousNames.has(field) || selectedNames.has(field)) return false;
            return getMetricFieldAttributes(field, i18n).category === 'basic';
          })
          .map((field) => ({ name: field, unit: getDefaultUnit(field, i18n) }));

        if (newMetrics.length > 0) {
          onChangeSelectedMetrics([...validMetrics, ...newMetrics]);
        }
      }

      return Array.from(fieldsSet).map((field) => {
        const units = getUnitsPerMetric(field, i18n);
        const oldOptionUnit = oldOptions.find((option) => option.name === field)?.unit ?? null;
        const defaultUnit = getDefaultUnit(field, i18n);
        const attrs = getMetricFieldAttributes(field, i18n);
        const label = attrs.dataLabel;

        return {
          name: field,
          label: label[0].toUpperCase() + label.slice(1),
          unit: oldOptionUnit ?? defaultUnit,
          options: units.map((unit) => ({ label: unit.label, value: unit.enumValue })),
          category: attrs.category,
        };
      });
    });
  }, [data, selectedDeviceIds, i18n]);

  const filteredMetricOptions = useMemo(() => {
    const sorted = [...metricOptions].sort((a, b) => a.label.localeCompare(b.label));
    if (searchMetrics) {
      const fuse = new Fuse(sorted, { keys: ['label'] });
      return fuse.search(searchMetrics).map(({ item }) => item);
    }
    return sorted;
  }, [metricOptions, searchMetrics]);

  const basicOptions = useMemo(
    () => filteredMetricOptions.filter((o) => o.category === 'basic'),
    [filteredMetricOptions],
  );

  const advancedOptions = useMemo(
    () => filteredMetricOptions.filter((o) => o.category === 'advanced'),
    [filteredMetricOptions],
  );

  const buildCheckboxProps = (options: MetricOption[]) => ({
    title: '' as ReactNode,
    options: options.map((option) => {
      const selectedMetric = selectedMetrics.find((metric) => option.name === metric.name);
      return {
        value: option.name,
        label: option.label,
        checked: selectedMetric !== undefined,
        select: {
          options: option.options,
          value: selectedMetric?.unit || option.unit,
          onChange(unit: MetricUnit, idx: number) {
            setMetricOptions((oldOptions) => {
              const { name } = options[idx] ?? {};
              return oldOptions.map((o) => (o.name === name ? { ...o, unit } : o));
            });
            onChangeSelectedMetrics(
              selectedMetrics.map((metric) =>
                metric.name === option.name ? { ...metric, unit } : { ...metric },
              ),
            );
          },
        },
      };
    }),
    onToggle(value: MetricField, checked: boolean) {
      const newMetrics = selectedMetrics.filter((metric) => metric.name !== value);
      if (checked) {
        const metricOption = metricOptions.find((o) => o.name === value);
        if (metricOption) {
          onChangeSelectedMetrics([
            ...newMetrics,
            { name: metricOption.name, unit: metricOption.unit },
          ]);
        }
      } else {
        onChangeSelectedMetrics([...newMetrics]);
      }
    },
    onSelectAll() {},
    onDeselectAll() {},
    selectAllDisabled: true,
    deselectAllDisabled: true,
    onSearch: setSearchMetrics,
    search: searchMetrics,
    selectedLength: 0,
    showControls: false as const,
    listHeight: options.length * ROW_HEIGHT,
  });

  const makeSelectAll = (options: MetricOption[]) => () => {
    const alreadySelected = new Set(selectedMetrics.map((m) => m.name));
    const toAdd = options
      .filter((o) => !alreadySelected.has(o.name))
      .map((o) => ({ name: o.name, unit: o.unit }));
    onChangeSelectedMetrics([...selectedMetrics, ...toAdd]);
  };

  const makeDeselectAll = (options: MetricOption[]) => () => {
    const names = new Set(options.map((o) => o.name));
    onChangeSelectedMetrics(selectedMetrics.filter((m) => !names.has(m.name)));
  };

  const isAllSelected = (options: MetricOption[]) =>
    options.length > 0 && options.every((o) => selectedMetrics.find((m) => m.name === o.name));

  const isNoneSelected = (options: MetricOption[]) =>
    !options.some((o) => selectedMetrics.find((m) => m.name === o.name));

  const renderAccordionSummary = (
    label: ReactNode,
    options: MetricOption[],
    expanded: boolean,
  ) => {
    const selectedCount = options.filter((o) =>
      selectedMetrics.find((m) => m.name === o.name),
    ).length;
    return (
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        px: 1,
        minHeight: 36,
        borderRadius: 1,
        bgcolor: expanded ? 'rgba(255,255,255,0.05)' : 'transparent',
        transition: 'background-color 0.2s',
        '& .MuiAccordionSummary-content': { my: 0, overflow: 'hidden' },
      }}
    >
      <Box display="flex" alignItems="center" minWidth={0} flex={1}>
        <Typography variant="body2" fontWeight="medium" sx={{ flexShrink: 0 }}>
          {label}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 1, flexShrink: 0 }}
        >
          {selectedCount}/{options.length}
        </Typography>
        <Box flex={1} />
        <Button
          size="small"
          variant="text"
          color="info"
          onClick={(e) => { e.stopPropagation(); makeSelectAll(options)(); }}
          disabled={isAllSelected(options)}
          sx={CATEGORY_BUTTON_SX}
        >
          <Trans>Todos</Trans>
        </Button>
        <Button
          size="small"
          variant="text"
          color="info"
          onClick={(e) => { e.stopPropagation(); makeDeselectAll(options)(); }}
          disabled={isNoneSelected(options)}
          sx={CATEGORY_BUTTON_SX}
        >
          <Trans>Ninguno</Trans>
        </Button>
      </Box>
    </AccordionSummary>
    );
  };

  return (
    <InnerPaper>
      <Box display="flex" gap={2} justifyContent="space-between" alignItems="center" mb={1}>
        <Typography fontWeight="bold">
          <Trans>Seleccionar métricas</Trans>
        </Typography>
        <Typography>
          <Plural value={selectedMetrics.length} one="# seleccionado" other="# seleccionados" />
        </Typography>
      </Box>
      <Box display="flex" gap={1} alignItems="center" mb={2}>
        <TextField
          onChange={(e) => setSearchMetrics(e.target.value)}
          value={searchMetrics}
          label={_(msg`Buscar`)}
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>

      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 'max(calc(100dvh - 480px), 180px)' }}>
        <Accordion
          expanded={basicExpanded || isSearching}
          onChange={(_, open) => {
            if (!isSearching) setBasicExpanded(open);
          }}
          disableGutters
          elevation={0}
          sx={{ bgcolor: 'transparent', overflow: 'hidden' }}
        >
          {renderAccordionSummary(<Trans>Estándar</Trans>, basicOptions, basicExpanded || isSearching)}
          <AccordionDetails sx={{ px: 0, py: 1 }}>
            <MultipleCheckbox {...buildCheckboxProps(basicOptions)} />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={advancedExpanded || isSearching}
          onChange={(_, open) => {
            if (!isSearching) setAdvancedExpanded(open);
          }}
          disableGutters
          elevation={0}
          sx={{ bgcolor: 'transparent', overflow: 'hidden' }}
        >
          {renderAccordionSummary(<Trans>Avanzado</Trans>, advancedOptions, advancedExpanded || isSearching)}
          <AccordionDetails sx={{ px: 0, py: 1 }}>
            <MultipleCheckbox {...buildCheckboxProps(advancedOptions)} />
          </AccordionDetails>
        </Accordion>
      </Box>
    </InnerPaper>
  );
};
